/**
 * @pj/protocol — the wire contract shared by client and (future) server.
 *
 * This is the multiplayer seam. Today a LocalRoom feeds these structures to the
 * client in-process; tomorrow a Durable Object serializes the exact same
 * structures over a WebSocket. Keep everything here plain and JSON-serializable
 * (no class instances, no functions on the data) so the seam stays clean.
 */

export const TILE_SIZE = 16;
/** Authoritative simulation rate. */
export const TICK_HZ = 60;
/** Ticks to traverse one tile walking — the GBA-ish stepped walk cadence (~133ms). */
export const STEP_TICKS = 8;
/** Ticks per tile while surfing — Gen 3 surf moves at running speed (2× walk). */
export const SURF_STEP_TICKS = 4;

export const PROTOCOL_VERSION = 3;

export type Dir = "up" | "down" | "left" | "right";

export const DIR_VECTORS: Record<Dir, { dx: number; dy: number }> = {
  up: { dx: 0, dy: -1 },
  down: { dx: 0, dy: 1 },
  left: { dx: -1, dy: 0 },
  right: { dx: 1, dy: 0 },
};

/** Client → room commands. Discriminated union, versioned by PROTOCOL_VERSION. */
export type Command =
  | { type: "join"; name: string }
  /**
   * The player's currently-held movement direction (null = released). Carries a
   * monotonic `seq` so a future networked client can reconcile prediction; the
   * LocalRoom ignores `seq` because it is already authoritative locally.
   */
  | { type: "intent"; seq: number; dir: Dir | null }
  /** A button: interact with the faced tile, or advance an open message box. */
  | { type: "interact" }
  /** Answer the open yes/no prompt (e.g. "Would you like to SURF?"). */
  | { type: "promptReply"; accept: boolean }
  /** Sudoku minigame: the player's current grid (81 chars, '0' = empty). The
   *  sim persists it and is the authority on "solved". */
  | { type: "sudokuEntries"; entries: string }
  /** Close the sudoku UI without finishing (progress is kept). */
  | { type: "sudokuExit" };

/** Room → client events: things the HUD reacts to that are not spatial state. */
export type RoomEvent =
  | { type: "joined"; actorId: string }
  /** Show one Gen-3 message box. `more` = a ▼-advance leads to another box. */
  | { type: "message"; lines: string[]; more: boolean }
  /** Show a yes/no prompt box alongside the given message lines. */
  | { type: "prompt"; lines: string[] }
  /** All message/prompt UI closed — the field resumes. */
  | { type: "uiClose" }
  | { type: "toast"; text: string }
  | {
      type: "objective";
      levelNum: number;
      levelName: string;
      label: string;
      progress: number;
      total: number;
      complete: boolean;
    }
  | { type: "progress"; exp: number; power: number; expForNext: number; abilities: string[] }
  /** Ability state changed (e.g. surf mounted/dismounted). */
  | { type: "ability"; ability: string; active: boolean }
  /** Level-2 countdown (null = hide the timer). Emitted ~once per second. */
  | { type: "timer"; remainingMs: number | null }
  /** Open the sudoku minigame UI with this attempt's puzzle state. */
  | { type: "sudoku"; seed: number; entries: string }
  /** Close the sudoku minigame UI. */
  | { type: "sudokuClose" };

export type EntityKind = "player" | "npc" | "treasure" | "sign" | "door";

/** One entity as seen on the wire. Pixel position is authoritative (sub-tile). */
export interface EntitySnap {
  id: string;
  kind: EntityKind;
  /** Logical tile coordinates (where the entity "is" for game logic). */
  tx: number;
  ty: number;
  /** Rendered pixel position (top-left of the tile-sized sprite). */
  px: number;
  py: number;
  facing: Dir;
  moving: boolean;
  /** Riding the surf mount (renders blob + sitting pose, bobbing). */
  surfing: boolean;
  /** Render hint: which sprite sheet to draw ("player", "key", "berry", …). */
  sprite: string;
}

export interface WorldSnapshot {
  tick: number;
  mapId: string;
  entities: Record<string, EntitySnap>;
}

/** Per-tick delta. Replaces a network framework's built-in state sync. */
export interface Diff {
  tick: number;
  mapId: string;
  upsert: EntitySnap[];
  remove: string[];
}

export function emptySnapshot(mapId: string): WorldSnapshot {
  return { tick: 0, mapId, entities: {} };
}

export function applyDiff(snap: WorldSnapshot, diff: Diff): WorldSnapshot {
  const entities: Record<string, EntitySnap> = { ...snap.entities };
  for (const e of diff.upsert) entities[e.id] = e;
  for (const id of diff.remove) delete entities[id];
  return { tick: diff.tick, mapId: diff.mapId, entities };
}

function entityChanged(a: EntitySnap, b: EntitySnap): boolean {
  return (
    a.tx !== b.tx ||
    a.ty !== b.ty ||
    a.px !== b.px ||
    a.py !== b.py ||
    a.facing !== b.facing ||
    a.moving !== b.moving ||
    a.surfing !== b.surfing ||
    a.sprite !== b.sprite ||
    a.kind !== b.kind
  );
}

/** Compute the delta from `prev` to `next`. Pure; used by both LocalRoom and DO. */
export function diffSnapshots(prev: WorldSnapshot, next: WorldSnapshot): Diff {
  const upsert: EntitySnap[] = [];
  for (const e of Object.values(next.entities)) {
    const p = prev.entities[e.id];
    if (!p || entityChanged(p, e)) upsert.push(e);
  }
  const remove: string[] = [];
  for (const id of Object.keys(prev.entities)) {
    if (!next.entities[id]) remove.push(id);
  }
  return { tick: next.tick, mapId: next.mapId, upsert, remove };
}
